<template>
  <div class="settings-page">
    <base-header class="pb-6 pb-7 pt-5 pt-md-7 bg-gradient">
      <h2 class="page-title">{{ $t('settings.title') }}</h2>
    </base-header>

    <b-container fluid class="mt--6">
      <b-row>
        <b-col xl="6" lg="8">
          <div class="settings-card">
            <div class="settings-card-header">
              <div class="header-icon">
                <i class="ni ni-key-25"></i>
              </div>
              <div class="header-text">
                <h3>{{ $t('settings.apiToken') }}</h3>
                <p>{{ $t('settings.apiTokenHint') }}</p>
              </div>
            </div>

            <div class="settings-card-body">
              <div class="token-display">
                <code class="token-value">{{ settings.token || '—' }}</code>
                <button
                  type="button"
                  class="copy-btn"
                  @click="copyToken"
                  :title="$t('settings.copy')"
                  :disabled="!settings.token"
                >
                  <i class="ni ni-single-copy-04"></i>
                </button>
              </div>
            </div>

            <div class="settings-card-footer">
              <button
                type="button"
                class="btn-reset"
                @click="RevokeToken"
              >
                <i class="ni ni-refresh-02"></i>
                {{ $t('settings.resetToken') }}
              </button>
            </div>
          </div>
        </b-col>

        <b-col xl="6" lg="8">
          <div class="settings-card">
            <div class="settings-card-header">
              <div class="header-icon">
                <i class="ni ni-send"></i>
              </div>
              <div class="header-text">
                <h3>{{ $t('settings.smsTelegram.title') }}</h3>
                <p>{{ $t('settings.smsTelegram.hint') }}</p>
              </div>
              <label
                class="status-switch"
                :class="{ 'active': smsTelegram.enabled }"
                role="switch"
                :aria-checked="smsTelegram.enabled"
                tabindex="0"
                @click="smsTelegram.enabled = !smsTelegram.enabled"
                @keydown.space.prevent="smsTelegram.enabled = !smsTelegram.enabled"
              >
                <span class="switch-track">
                  <span class="switch-thumb"></span>
                </span>
                <span class="switch-label">
                  {{ smsTelegram.enabled ? $t('common.on') : $t('common.off') }}
                </span>
              </label>
            </div>

            <div class="settings-card-body">
              <div class="form-group">
                <label class="form-label">{{ $t('settings.smsTelegram.botToken') }}</label>
                <input
                  type="password"
                  class="form-control"
                  :placeholder="smsTelegram.saved_bot_token || $t('settings.smsTelegram.botTokenPlaceholder')"
                  v-model="smsTelegram.bot_token"
                  autocomplete="new-password"
                >
                <small v-if="smsTelegram.saved_bot_token" class="form-hint">
                  {{ $t('settings.smsTelegram.botTokenKeep') }}
                </small>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('settings.smsTelegram.chatId') }}</label>
                <input
                  type="text"
                  class="form-control"
                  :placeholder="$t('settings.smsTelegram.chatIdPlaceholder')"
                  v-model="smsTelegram.chat_id"
                  autocomplete="off"
                >
                <small class="form-hint">{{ $t('settings.smsTelegram.chatIdHint') }}</small>
              </div>
            </div>

            <div class="settings-card-footer">
              <button
                type="button"
                class="btn-action btn-save"
                @click="saveSmsTelegram"
                :disabled="smsTelegram.saving"
              >
                <i class="ni ni-check-bold"></i>
                {{ $t('common.save') }}
              </button>
              <button
                type="button"
                class="btn-action btn-test"
                @click="testSmsTelegram"
                :disabled="!canTestSmsTelegram || smsTelegram.testing"
                :title="canTestSmsTelegram ? '' : $t('settings.smsTelegram.testNeedsSave')"
              >
                <i class="ni ni-send"></i>
                {{ $t('settings.smsTelegram.test') }}
              </button>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>
<script>
import axios from 'axios'

export default {
  data() {
    return {
      settings: {
          "token": null
      },
      smsTelegram: {
        enabled: false,
        bot_token: '',
        chat_id: '',
        // State as stored on the server; the token comes back masked only
        saved_bot_token: null,
        saved_chat_id: null,
        saving: false,
        testing: false
      }
    }
  },
  computed: {
    // The test is sent with the saved settings, so unsaved edits must be saved first
    canTestSmsTelegram() {
      return !!this.smsTelegram.saved_bot_token
        && !!this.smsTelegram.saved_chat_id
        && !this.smsTelegram.bot_token.trim()
        && this.smsTelegram.chat_id.trim() === this.smsTelegram.saved_chat_id;
    }
  },
  methods: {
    async Settings() {
      const response = await axios.get('dashboard/settings')
      this.settings.token = response.data.token
      this.applySmsTelegram(response.data.sms_telegram)
    },
    applySmsTelegram(data) {
      if (!data) return;
      this.smsTelegram.enabled = !!data.enabled
      this.smsTelegram.bot_token = ''
      this.smsTelegram.chat_id = data.chat_id || ''
      this.smsTelegram.saved_bot_token = data.bot_token
      this.smsTelegram.saved_chat_id = data.chat_id
    },
    notifyError(message) {
      this.$notify({
        title: this.$t('common.error'),
        message: message,
        type: 'danger'
      });
    },
    async saveSmsTelegram() {
      const botToken = this.smsTelegram.bot_token.trim();
      const chatId = this.smsTelegram.chat_id.trim();

      if (this.smsTelegram.enabled && (!(botToken || this.smsTelegram.saved_bot_token) || !chatId)) {
        this.notifyError(this.$t('settings.smsTelegram.required'));
        return;
      }

      let res;
      this.smsTelegram.saving = true;
      try {
        res = await axios.put('dashboard/settings/sms-telegram', {
          enabled: this.smsTelegram.enabled,
          bot_token: botToken || null,
          chat_id: chatId || null
        })
      } catch (e) {
        if (e.response && e.response.status === 401) {
          await this.$router.push({name: 'login'});
          return;
        }
        this.notifyError(e.message);
        return;
      } finally {
        this.smsTelegram.saving = false;
      }

      if (res.data.status === true) {
        this.applySmsTelegram(res.data.sms_telegram);
        this.$notify({
          title: this.$t('common.success'),
          message: this.$t('settings.smsTelegram.saved'),
          type: 'success'
        });
      } else {
        this.notifyError(res.data.message);
      }
    },
    async testSmsTelegram() {
      let res;
      this.smsTelegram.testing = true;
      try {
        res = await axios.post('dashboard/settings/sms-telegram/test')
      } catch (e) {
        if (e.response && e.response.status === 401) {
          await this.$router.push({name: 'login'});
          return;
        }
        this.notifyError(e.message);
        return;
      } finally {
        this.smsTelegram.testing = false;
      }

      if (res.data.status === true) {
        this.$notify({
          title: this.$t('common.success'),
          message: this.$t('settings.smsTelegram.testSent'),
          type: 'success'
        });
      } else {
        this.notifyError(res.data.message);
      }
    },
    async RevokeToken() {
      if (confirm(this.$t('settings.confirmReset'))) {
        const response = await axios.post('dashboard/settings/revoke')
        if (response.data.status === true) {
          this.$notify({
            title: this.$t('common.success'),
            message: this.$t('settings.tokenReset'),
            type: 'success'
          });
        }
        this.settings.token = response.data.token
      }
    },
    async copyToken() {
      if (!this.settings.token) return;
      try {
        await navigator.clipboard.writeText(this.settings.token);
        this.$notify({
          title: this.$t('common.copied'),
          message: this.$t('settings.tokenCopied'),
          type: 'success'
        });
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  },
  mounted() {
    this.Settings();
  },
  created () {
  },
};
</script>

<style scoped lang="scss">
.settings-page {
  .page-title {
    color: #fff;
    font-size: 2rem;
    font-weight: 700;
    margin: 0;
  }
}

.bg-gradient {
  background: linear-gradient(87deg, #172b4d 0, #1a174d 100%) !important;
}

.settings-card {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
  margin-bottom: 1.5rem;
}

.settings-card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border-bottom: 1px solid #f0f0f0;

  .header-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(67, 97, 238, 0.1);
    color: #4361EE;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 1.1rem;
    }
  }

  .header-text {
    h3 {
      font-size: 1rem;
      font-weight: 600;
      color: #333;
      margin: 0 0 0.25rem 0;
    }

    p {
      font-size: 0.8rem;
      color: #909399;
      margin: 0;
    }
  }
}

.settings-card-body {
  padding: 1.25rem;

  .form-group {
    margin-bottom: 0.875rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .form-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: #909399;
    margin-bottom: 0.3rem;
  }

  .form-control {
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background-color: #fff;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: #4361EE;
    }

    &::placeholder {
      color: #c0c4cc;
    }
  }

  .form-hint {
    display: block;
    font-size: 0.72rem;
    color: #909399;
    margin-top: 0.25rem;
  }
}

.status-switch {
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  margin-left: auto;
  padding: 0.375rem 0.625rem 0.375rem 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .switch-track {
    position: relative;
    width: 28px;
    height: 16px;
    background-color: #e4e7ed;
    border-radius: 8px;
    transition: background-color 0.2s ease;
    flex-shrink: 0;
  }

  .switch-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 12px;
    height: 12px;
    background-color: #fff;
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .switch-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #909399;
    transition: color 0.15s ease;
    white-space: nowrap;
  }

  &.active {
    .switch-track {
      background-color: #52c41a;
    }

    .switch-thumb {
      transform: translateX(12px);
    }

    .switch-label {
      color: #52c41a;
    }
  }

  &:focus {
    outline: none;
    background-color: rgba(67, 97, 238, 0.08);
  }
}

.token-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 0.75rem 1rem;

  .token-value {
    flex: 1;
    font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
    font-size: 0.85rem;
    color: #333;
    word-break: break-all;
    background: none;
    padding: 0;
  }

  .copy-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: #909399;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
    flex-shrink: 0;

    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.06);
      color: #606266;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    i {
      font-size: 0.9rem;
    }
  }
}

.settings-card-footer {
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;

  .btn-action {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    background: transparent;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    i {
      font-size: 0.9rem;
    }
  }

  .btn-save {
    color: #4361EE;

    &:hover:not(:disabled) {
      background: rgba(67, 97, 238, 0.1);
    }
  }

  .btn-test {
    color: #11998e;

    &:hover:not(:disabled) {
      background: rgba(17, 153, 142, 0.08);
    }
  }

  .btn-reset {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #f56c6c;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background: rgba(245, 108, 108, 0.1);
    }

    i {
      font-size: 0.9rem;
    }
  }
}
</style>
